import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Send, Bot } from "lucide-react";

const LINES = [
  { parts: [{ t: "HR that feels" }] },
  { parts: [{ t: "less like ", c: "" }, { t: "paperwork,", c: "text-[#38BDF8]" }] },
  { parts: [{ t: "more like ", c: "" }, { t: "people.", c: "text-[#FF5722]" }] },
];

export default function HeroSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMascot = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const [prompt, setPrompt] = useState("");

  const askQuick = (e) => {
    e.preventDefault();
    const q = prompt.trim() || "How do I onboard managers across 4 timezones?";
    window.location.hash = "#ai-tools";
    window.dispatchEvent(new CustomEvent("opera360:quick-ask", { detail: q }));
  };

  return (
    <section id="top" ref={ref} className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden bg-[#0B1B33]">
      <motion.div style={{ y: yBlob }} className="absolute -top-24 -right-32 w-[34rem] h-[34rem] rounded-full bg-[#2563EB]/25 blur-3xl" />
      <div className="absolute top-40 -left-40 w-[28rem] h-[28rem] rounded-full bg-[#38BDF8]/15 blur-3xl" />
      <div className="absolute bottom-0 left-1/3 w-[24rem] h-[24rem] rounded-full bg-[#FF5722]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-14 items-center relative">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-sky-300 border border-white/15 backdrop-blur"
          >
            <span className="w-2 h-2 rounded-full bg-[#FF5722] animate-pulse-dot" />
            Powered by ChatGPT + Claude · Links to Opera360 Trains
          </motion.div>

          <h1 data-testid="hero-title-masked" className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-7xl mt-6 text-white">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.25 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.parts.map((p, j) => (
                    <span key={j} className={p.c}>{p.t}</span>
                  ))}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="text-lg sm:text-xl text-slate-300 leading-relaxed mt-6 max-w-xl"
          >
            Opera360 HR turns onboarding, compliance, and people operations into a joyful,
            intelligent flow — drafted by Claude, answered by ChatGPT, and wired straight into
            the Opera360 training standard.
          </motion.p>

          <motion.form
            onSubmit={askQuick}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-8 flex items-center gap-2 max-w-xl bg-white/5 backdrop-blur rounded-full border border-white/15 shadow-[0_12px_40px_-15px_rgba(0,0,0,0.5)] p-2 pl-5"
          >
            <Bot className="w-5 h-5 text-[#38BDF8] shrink-0" />
            <input
              data-testid="hero-quick-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ask HR anything…"
              className="flex-1 bg-transparent outline-none text-sm sm:text-base font-medium text-white placeholder:text-slate-500"
            />
            <button
              data-testid="hero-quick-prompt-submit"
              type="submit"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#FF5722] text-white text-sm font-bold hover:bg-[#38BDF8] hover:text-[#0B1B33] transition-colors duration-300"
            >
              Ask <Send className="w-3.5 h-3.5" />
            </button>
          </motion.form>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-primary-cta"
              href="#crm-demo"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#FF5722] text-white font-bold text-sm hover:bg-white hover:text-[#0B1B33] hover:gap-3.5 transition-all duration-300"
            >
              Book a live demo <ArrowRight className="w-4 h-4" />
            </a>
            <a
              data-testid="hero-secondary-cta"
              href="https://www.opera360trains.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/20 font-bold text-sm text-slate-200 hover:border-[#38BDF8] hover:text-[#38BDF8] transition-colors duration-300"
            >
              Explore Opera360 Trains ↗
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
            className="mt-10 flex flex-wrap gap-6 text-sm"
          >
            {[["-41%", "manager churn"], ["12k+", "docs drafted"], ["24/7", "AI concierge"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-display font-black text-2xl text-white">{v}</div>
                <div className="text-slate-400 text-xs uppercase tracking-widest font-bold">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div style={{ y: yMascot }} className="relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, scale: 0.85, rotate: 6 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute inset-0 rounded-[2.5rem] bg-gradient-to-br from-[#38BDF8] to-[#FF5722] rotate-3 scale-105 opacity-25" />
            <div className="relative rounded-[2.5rem] overflow-hidden border-2 border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
              <img
                src="https://images.unsplash.com/photo-1740252117070-7aa2955b25f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxjb2xvcmZ1bCUyMGF2YXRhciUyMGNoYXJhY3RlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc5MDQ1MDE4Mnww&ixlib=rb-4.1.0&q=85&w=900"
                alt="Dexter, the Opera360 HR copilot character"
                className="w-full aspect-[4/5] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-8 bg-white rounded-2xl border-2 border-slate-900/5 shadow-xl px-5 py-4 animate-floaty">
              <div className="flex items-center gap-3">
                <span className="w-2.5 h-2.5 rounded-full bg-[#10B981] animate-pulse-dot" />
                <div>
                  <div className="font-display font-bold text-sm text-slate-900">Dexter is live</div>
                  <div className="text-xs text-slate-500">Drafting an offer letter…</div>
                </div>
              </div>
            </div>
            <div className="absolute -top-5 -right-6 bg-white/10 backdrop-blur border border-white/15 text-white rounded-2xl shadow-xl px-5 py-3 animate-floaty" style={{ animationDelay: "1.2s" }}>
              <div className="font-display font-bold text-sm">Fair-housing quiz</div>
              <div className="text-xs text-emerald-400 font-bold">Score 98% ✓ certified</div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
